import { useState } from 'react';
import {
  Box, Button, Checkbox, CircularProgress, FormControlLabel, FormLabel, InputAdornment, TextField,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock, faUser } from '@fortawesome/free-solid-svg-icons';
import Cookies from 'js-cookie';
import { LoginRequest } from '../../services/authentication.types';
import { loginSchema } from '../../validation';
import { useUserContext, UserRole } from '../../contexts/UserContext';
import api from '../../services/api';
import config from '../../config';
import { encrypt } from '../../utils/encryption';
import TwoFactorAuthModal from '../../components/Modals/TwoFactorAuthModal';
import styles from './Login.module.css';

interface PendingLoginData {
  email: string;
  remember: boolean;
}

function Login() {
  const { errorMessage, validationErrors } = useUserContext();
  const navigate = useNavigate();

  const [rememberPassword, setRememberPassword] = useState(true);
  const [showTwoFactorModal, setShowTwoFactorModal] = useState(false);
  const [twoFactorError, setTwoFactorError] = useState('');
  const [twoFactorLoading, setTwoFactorLoading] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  const [pendingLoginData, setPendingLoginData] = useState<PendingLoginData | null>(null);
  const [localErrorMessage, setLocalErrorMessage] = useState('');
  const [localValidationErrors, setLocalValidationErrors] = useState<string[]>([]);
  const [isAccountLocked, setIsAccountLocked] = useState(false);
  const [showAccountLockedCard, setShowAccountLockedCard] = useState(false);

  const validationFailed = validationErrors.length > 0 || localValidationErrors.length > 0;
  const { register, handleSubmit, formState: { errors } } = useForm<LoginRequest>({
    mode: 'onSubmit',
    resolver: yupResolver(loginSchema),
    criteriaMode: 'all',
    defaultValues: {
      id: '',
      password: '',
      remember: rememberPassword,
    },
  });

  const onSubmitHandler = handleSubmit(async (data: LoginRequest) => {
    setLoginLoading(true);
    setLocalErrorMessage('');
    setLocalValidationErrors([]);
    setShowAccountLockedCard(false); // Clear locked card state on new login attempt

    try {
      const response = await api.post('/auth/login', {
        mail: data.id,
        password: data.password,
      });

      if (response.data?.requires_two_factor) {
        setIsAccountLocked(false);
        setPendingLoginData({ email: data.id, remember: data.remember });
        setShowTwoFactorModal(true);
        return;
      }

      const { user_roles } = response.data;

      if (user_roles !== UserRole.USER) {
        setLocalErrorMessage('No permission');
        return;
      }

      setPendingLoginData({ email: data.id, remember: data.remember });
      setShowTwoFactorModal(true);
    } catch (error: any) {
      if (error?.response?.data?.error) {
        setLocalValidationErrors(Array.isArray(error.response.data.error)
          ? error.response.data.error
          : [error.response.data.error]);
        return;
      }

      const errorMessage = error?.response?.data?.message
        || (typeof error?.response?.data === 'string' ? error?.response?.data : null)
        || error?.response?.data?.error
        || error?.message
        || 'Login failed';

      const errorText = typeof errorMessage === 'string' ? errorMessage : String(errorMessage);

      // Only show locked account card if backend explicitly says account is locked
      // Check is_account_locked flag from backend response, not just error message text
      const isAccountLocked = error?.response?.data?.is_account_locked ?? false;

      if (isAccountLocked) {
        setShowAccountLockedCard(true);
        setShowTwoFactorModal(false);
        setLocalErrorMessage('');
        return;
      }

      setLocalErrorMessage(errorMessage);
    } finally {
      setLoginLoading(false);
    }
  });

  const handleResendTwoFactorCode = async () => {
    if (!pendingLoginData) {
      setTwoFactorError('Login information not found');
      return;
    }

    try {
      await api.post('/auth/resend-two-factor-code', {
        email: pendingLoginData.email,
      });
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message
        || error?.response?.data?.error
        || error?.message
        || 'Failed to send authentication code';
      setTwoFactorError(errorMessage);
      throw error;
    }
  };

  const handleVerifyTwoFactor = async (code: string) => {
    if (!pendingLoginData) {
      setTwoFactorError('Login information not found');
      return;
    }

    setTwoFactorLoading(true);
    setTwoFactorError('');

    try {
      const response = await api.post('/auth/verify-two-factor', {
        email: pendingLoginData.email,
        code: code.trim(),
      });

      const {
        token,
        user_roles,
        user,
        webdb_key,
      } = response.data;

      if (user_roles !== UserRole.USER) {
        setTwoFactorError('No permission');
        return;
      }

      const authorityLogin = {
        authorityList: [{
          authority_feature: 'user_logged',
        }],
      };

      const authorities = [authorityLogin];
      const { cookieName } = config.auth;

      const cookieRawValue = {
        token,
        user,
        authorities,
        userRoles: user_roles,
      };

      const cookieValue = encrypt(JSON.stringify(cookieRawValue));

      Cookies.set(cookieName, cookieValue, {
        secure: config.env === 'production',
        expires: pendingLoginData.remember ? 365 : null,
      });

      if (webdb_key && webdb_key !== '') {
        localStorage.setItem(config.api.webdbKeyStorage, webdb_key);
        setShowTwoFactorModal(false);
        setPendingLoginData(null);
        location.href = '/';
      } else {
        setTwoFactorError('Failed to get Webdb key. Please try logging in again.');
      }
    } catch (error: any) {
      const isAccountLocked = error?.response?.data?.is_account_locked ?? false;

      // Check if account is locked based on backend's is_account_locked flag
      // If modal is open and account gets locked, show locked state in modal
      if (isAccountLocked) {
        setIsAccountLocked(true);
        setTwoFactorError('');
        setTwoFactorLoading(false);
        return;
      }

      // Handle error messages (account lock is already handled above via is_account_locked flag)
      if (error?.response?.data?.message) {
        setTwoFactorError(error.response.data.message);
      } else if (error?.response?.data?.error) {
        const errorData = error.response.data.error;
        setTwoFactorError(typeof errorData === 'string' ? errorData : String(errorData));
      } else if (error?.response?.data?.errors) {
        // Handle validation errors
        const { errors: validationErrors } = error.response.data;
        const firstError = Object.values(validationErrors)[0];
        const errorMsg = Array.isArray(firstError) ? firstError[0] : firstError;
        setTwoFactorError(errorMsg || 'Authentication code is incorrect');
      } else {
        setTwoFactorError('Authentication code is incorrect');
      }
    } finally {
      setTwoFactorLoading(false);
    }
  };

  const onRegisterNewCustomer = () => navigate('/verify-email');
  const onForgotPassword = () => navigate('/verify-email/forgot-password');

  return (
    <>
      <Helmet>
        <title>BUILD-SELF</title>
      </Helmet>
      <main className="d-flex w-100 login-background">
        <div className="container d-flex flex-column">
          <div className="vh-100">
            <div className="login-box mx-auto d-table h-100">
              <div className="d-table-cell align-middle">

                <div className="text-center my-2">
                  <div>
                    <img src="/img/logo.png" alt="BUILD-SELF" className="img-fluid mb-2" width="320" height="64" />
                  </div>
                </div>

                <Box className="card mb-0" style={{ fontSize: '0.8rem' }}>
                  <Box className="card-body">
                    <Box className="mx-sm-3">
                      <Box className="text-center">
                        <FormLabel className="login-box-msg text-sm" sx={{ marginBottom: 2 }}>
                          Please enter user ID and password
                        </FormLabel>

                        {showAccountLockedCard && (
                          <Box className="alert alert-danger mb-3" role="alert">
                            Your account has been locked due to too many login attempts.
                          </Box>
                        )}

                        {validationFailed && !showAccountLockedCard && (
                          <Box className="alert alert-danger mb-3" role="alert">
                            Login ID or password is incorrect
                          </Box>
                        )}

                        {(errorMessage || localErrorMessage) && !showAccountLockedCard && (
                          <Box className="alert alert-danger mb-3" role="alert">
                            {errorMessage || localErrorMessage}
                          </Box>
                        )}

                      </Box>
                      <Box>

                        <TextField
                          {...register('id')}
                          label="Login ID"
                          placeholder="Enter login ID"
                          variant="outlined"
                          error={!!errors.id}
                          helperText={errors.id?.message || ' '}
                          fullWidth
                          sx={{ marginBottom: 1 }}
                          size="small"
                          InputProps={{
                            endAdornment: <InputAdornment position="end"><FontAwesomeIcon icon={faUser} /></InputAdornment>,
                          }}
                        />

                        <TextField
                          {...register('password')}
                          label="Password"
                          placeholder="Enter password"
                          type="password"
                          variant="outlined"
                          error={!!errors.password}
                          helperText={errors.password?.message || ' '}
                          fullWidth
                          size="small"
                          InputProps={{
                            endAdornment: <InputAdornment position="end"><FontAwesomeIcon icon={faLock} /></InputAdornment>,
                          }}
                        />

                        <FormControlLabel
                          label="Keep me logged in"
                          sx={{ alignItems: 'center', marginBottom: 1 }}
                          control={(
                            <Checkbox
                              {...register('remember')}
                              id="remember"
                              checked={rememberPassword}
                              onChange={() => setRememberPassword(!rememberPassword)}
                            />
                          )}
                        />

                        <Button
                          style={{ fontSize: '0.8rem' }}
                          sx={{ width: '100%' }}
                          onClick={onSubmitHandler}
                          disabled={loginLoading}
                          variant="contained"
                        >
                          {!loginLoading ? 'Login' : (<CircularProgress size={20} />)}
                        </Button>

                      </Box>

                      <Button
                        style={{
                          fontSize: '0.8rem',
                          marginTop: 10,
                        }}
                        sx={{ width: '100%' }}
                        onClick={onRegisterNewCustomer}
                        variant="contained"
                        color="success"
                      >
                        Register
                      </Button>

                      <Box
                        style={{ textAlign: 'center', marginTop: 10 }}
                        onClick={onForgotPassword}
                      >
                        <a>Forgot your password?</a>
                      </Box>
                    </Box>
                  </Box>
                </Box>
                <div className="version mt-1 mx-1">version 0.0.1</div>

              </div>
            </div>
          </div>
        </div>
      </main>

      <TwoFactorAuthModal
        show={showTwoFactorModal}
        onVerify={handleVerifyTwoFactor}
        onResendCode={handleResendTwoFactorCode}
        onClose={() => {
          setShowTwoFactorModal(false);
          setPendingLoginData(null);
          setIsAccountLocked(false);
          setShowAccountLockedCard(false);
        }}
        loading={twoFactorLoading}
        errorMessage={twoFactorError}
        isAccountLocked={isAccountLocked}
      />
    </>

  );
}

export default Login;
